import { useMemo, useState } from 'react';
import { AlertTriangle, CheckCircle2, Landmark } from 'lucide-react';
import { FileDropzone } from './FileDropzone';
import { useCommitBankMatches, usePreviewBankStatement } from '../../hooks/useOnboarding';
import type { BankCommitLine, BankPreview, MatchResult } from '../../lib/onboardingApi';

// Step 4 — Bank statement: upload a CSV/XLSX statement, see it matched against
// open invoices in three colour-coded buckets (green / yellow / red).

const RON = new Intl.NumberFormat('ro-RO', { style: 'currency', currency: 'RON' });

function money(n: number): string {
  return RON.format(n);
}

/**
 * Turn the categorised preview into the lines the commit endpoint expects. A
 * result that resolved to an invoice (matched + needs-review) is sent as a
 * `confirm`; an unmatched line is sent as `ignore` so it is recorded but never
 * reconciled. The server re-verifies each invoice is still open before settling.
 */
function toCommitLines(preview: BankPreview): BankCommitLine[] {
  const lines: BankCommitLine[] = [];
  for (const m of [...preview.matched, ...preview.needsReview]) {
    lines.push({
      id: m.line.id,
      date: m.line.date,
      description: m.line.description,
      amount: m.line.amount,
      type: m.line.type,
      action: m.invoice ? 'confirm' : 'ignore',
      invoiceId: m.invoice?.id ?? null,
      confidence: m.score,
    });
  }
  for (const m of preview.unmatched) {
    lines.push({
      id: m.line.id,
      date: m.line.date,
      description: m.line.description,
      amount: m.line.amount,
      type: m.line.type,
      action: 'ignore',
      invoiceId: null,
      confidence: m.score,
    });
  }
  return lines;
}

type Bucket = 'matched' | 'needsReview' | 'unmatched';

const BUCKET_STYLE: Record<
  Bucket,
  { label: string; border: string; head: string; row: string; chip: string }
> = {
  matched: {
    label: 'Asociate automat',
    border: 'border-green-200',
    head: 'bg-green-50 text-green-700',
    row: 'text-green-900',
    chip: 'bg-green-100 text-green-800',
  },
  needsReview: {
    label: 'De verificat',
    border: 'border-amber-200',
    head: 'bg-amber-50 text-amber-700',
    row: 'text-amber-900',
    chip: 'bg-amber-100 text-amber-800',
  },
  unmatched: {
    label: 'Neasociate',
    border: 'border-red-200',
    head: 'bg-red-50 text-red-700',
    row: 'text-red-900',
    chip: 'bg-red-100 text-red-800',
  },
};

function MatchRow({ m, bucket }: { m: MatchResult; bucket: Bucket }) {
  const style = BUCKET_STYLE[bucket];
  return (
    <tr className={`align-top ${style.row}`}>
      <td className="whitespace-nowrap px-3 py-2 text-xs">{m.line.date}</td>
      <td className="px-3 py-2">
        <p className="text-sm">{m.line.description}</p>
        <span className={`mt-1 inline-block rounded px-1.5 py-0.5 text-[11px] ${style.chip}`}>
          {m.line.type === 'credit' ? 'încasare' : 'plată'}
        </span>
      </td>
      <td className="whitespace-nowrap px-3 py-2 text-right text-sm font-medium">
        {money(m.line.amount)}
      </td>
      <td className="px-3 py-2 text-sm">
        {m.invoice ? (
          <div>
            <p className="font-medium">{m.invoice.number}</p>
            {m.invoice.partnerName && (
              <p className="text-xs text-gray-500">{m.invoice.partnerName}</p>
            )}
            <p className="text-xs text-gray-500">{money(m.invoice.total)}</p>
          </div>
        ) : (
          <span className="text-xs text-gray-400">—</span>
        )}
      </td>
      <td className="px-3 py-2 text-xs">{m.reason}</td>
    </tr>
  );
}

function BucketTable({ bucket, rows }: { bucket: Bucket; rows: MatchResult[] }) {
  const style = BUCKET_STYLE[bucket];
  return (
    <div className={`overflow-hidden rounded-md border ${style.border}`}>
      <div className={`px-3 py-2 text-sm font-semibold ${style.head}`}>
        {style.label} ({rows.length})
      </div>
      {rows.length === 0 ? (
        <p className="px-3 py-3 text-sm text-gray-500">Nicio tranzacție în această categorie.</p>
      ) : (
        <table className="min-w-full divide-y divide-gray-100 text-left">
          <thead className="bg-white text-xs font-medium uppercase tracking-wide text-gray-500">
            <tr>
              <th className="px-3 py-2">Dată</th>
              <th className="px-3 py-2">Descriere</th>
              <th className="px-3 py-2 text-right">Sumă</th>
              <th className="px-3 py-2">Factură</th>
              <th className="px-3 py-2">Motiv</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-50 bg-white">
            {rows.map((m) => (
              <MatchRow key={m.line.id} m={m} bucket={bucket} />
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

function Totals({ preview }: { preview: BankPreview }) {
  const cells: { label: string; n: number; cls: string }[] = [
    { label: 'Total', n: preview.totals.total, cls: 'text-gray-900' },
    { label: 'Asociate', n: preview.totals.matched, cls: 'text-green-700' },
    { label: 'De verificat', n: preview.totals.needsReview, cls: 'text-amber-700' },
    { label: 'Neasociate', n: preview.totals.unmatched, cls: 'text-red-700' },
  ];
  return (
    <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
      {cells.map((c) => (
        <div
          key={c.label}
          className="rounded-md border border-gray-200 bg-white px-3 py-2 text-center"
        >
          <p className="text-xs text-gray-500">{c.label}</p>
          <p className={`text-lg font-semibold ${c.cls}`}>{c.n}</p>
        </div>
      ))}
    </div>
  );
}

export function StepBank() {
  const [file, setFile] = useState<File | null>(null);
  const [preview, setPreview] = useState<BankPreview | null>(null);
  const previewMutation = usePreviewBankStatement();
  const commitMutation = useCommitBankMatches();

  function handleFile(f: File) {
    setFile(f);
    setPreview(null);
    commitMutation.reset();
    previewMutation.mutate(f, { onSuccess: (data) => setPreview(data) });
  }

  // The lines we'd persist: matched + needs-review with an invoice become
  // confirmations, everything else is ignored. Memoised so the count is stable.
  const commitLines = useMemo(() => (preview ? toCommitLines(preview) : []), [preview]);
  const confirmCount = commitLines.filter((l) => l.action === 'confirm').length;
  const committed = commitMutation.data;

  function handleCommit() {
    if (!preview) return;
    commitMutation.mutate({
      bankCode: preview.bankCode,
      format: preview.format,
      fileName: file?.name ?? null,
      // Carried from preview so the server can reject a duplicate file (409).
      contentHash: preview.contentHash ?? null,
      lines: commitLines,
    });
  }

  return (
    <div className="space-y-5">
      <section className="rounded-lg border border-gray-200 bg-white p-5">
        <div className="flex items-start gap-3">
          <div className="rounded-md bg-brand-50 p-2 text-brand-600">
            <Landmark className="h-5 w-5" />
          </div>
          <div>
            <h3 className="text-sm font-semibold text-gray-900">Extras bancar</h3>
            <p className="mt-1 text-sm text-gray-500">
              Încărcați un extras bancar (CSV sau XLSX). Îl asociem cu facturile deschise și vă
              arătăm rezultatele grupate după nivelul de încredere.
            </p>
          </div>
        </div>
      </section>

      <FileDropzone
        accept=".csv,.xlsx"
        hint="Fișiere .csv sau .xlsx exportate din bancă"
        onFile={handleFile}
        busyFile={previewMutation.isPending ? file : null}
        busyLabel="Se analizează extrasul…"
      />

      {file && !previewMutation.isPending && (
        <p className="text-sm text-gray-600">
          Fișier selectat: <span className="font-medium text-gray-900">{file.name}</span>
        </p>
      )}
      {previewMutation.isError && (
        <p className="text-sm text-red-600">
          {previewMutation.error instanceof Error
            ? previewMutation.error.message
            : 'Extrasul nu a putut fi citit.'}
        </p>
      )}

      {preview && (
        <div className="space-y-4">
          <div className="flex flex-wrap items-center gap-2 text-xs text-gray-500">
            <span className="rounded-full border border-gray-200 bg-gray-50 px-2 py-1 font-medium text-gray-700">
              Bancă: {preview.bankCode}
            </span>
            <span className="rounded-full border border-gray-200 bg-gray-50 px-2 py-1 font-medium text-gray-700">
              Format: {preview.format}
            </span>
          </div>

          <Totals preview={preview} />

          {preview.warnings.length > 0 && (
            <div className="rounded-md border border-amber-200 bg-amber-50 p-3">
              <p className="flex items-center gap-2 text-sm font-semibold text-amber-800">
                <AlertTriangle className="h-4 w-4" />
                Avertismente
              </p>
              <ul className="mt-2 list-inside list-disc text-sm text-amber-700">
                {preview.warnings.map((w, i) => (
                  <li key={i}>{w}</li>
                ))}
              </ul>
            </div>
          )}

          <BucketTable bucket="matched" rows={preview.matched} />
          <BucketTable bucket="needsReview" rows={preview.needsReview} />
          <BucketTable bucket="unmatched" rows={preview.unmatched} />

          {committed ? (
            <div className="rounded-md border border-green-200 bg-green-50 p-4">
              <p className="flex items-center gap-2 text-sm font-semibold text-green-800">
                <CheckCircle2 className="h-4 w-4" />
                Extras salvat
              </p>
              <p className="mt-1 text-sm text-green-700">
                {committed.invoicesMarkedPaid}{' '}
                {committed.invoicesMarkedPaid === 1 ? 'factură marcată' : 'facturi marcate'} ca
                încasate/plătite · {committed.confirmedRows} confirmate · {committed.ignoredRows}{' '}
                ignorate · {committed.totalRows} tranzacții salvate.
              </p>
            </div>
          ) : (
            <div className="flex flex-col gap-2 border-t border-gray-100 pt-4">
              {commitMutation.isError && (
                <p className="text-sm text-red-600">
                  {commitMutation.error instanceof Error
                    ? commitMutation.error.message
                    : 'Extrasul nu a putut fi salvat.'}
                </p>
              )}
              <div className="flex items-center justify-between gap-3">
                <p className="text-sm text-gray-500">
                  Confirmați {confirmCount} {confirmCount === 1 ? 'asociere' : 'asocieri'} și
                  marcați facturile ca încasate/plătite. Tranzacțiile neasociate sunt salvate, dar
                  nu sunt reconciliate.
                </p>
                <button
                  type="button"
                  onClick={handleCommit}
                  disabled={commitMutation.isPending || confirmCount === 0}
                  className="shrink-0 rounded-md bg-brand-600 px-4 py-2 text-sm font-semibold text-white hover:bg-brand-700 disabled:cursor-not-allowed disabled:opacity-50"
                >
                  {commitMutation.isPending ? 'Se salvează...' : 'Confirmă tot'}
                </button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
